import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingCart, ChevronRight, Truck, Shield, RotateCcw, Minus, Plus, Check, Award, Recycle, CheckCircle, Info } from 'lucide-react';
import { productAPI } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';

const conditionLabels = {
  new_aftermarket: { label: 'Nuevo Aftermarket', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, desc: 'Parte nueva de reemplazo aftermarket' },
  used_oem: { label: 'Usado OEM', color: 'bg-amber-100 text-amber-700', icon: Recycle, desc: 'Parte original usada reciclada' },
  refurbished: { label: 'Reacondicionado', color: 'bg-blue-100 text-blue-700', icon: Award, desc: 'Restaurado profesionalmente a condicion funcional' },
};

export default function ProductDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setLoading(true);
    productAPI.getProduct(id).then(r => setProduct(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const handleAdd = async () => {
    if (!user) { window.location.href = '/login'; return; }
    try {
      await addToCart(product.id, qty);
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    } catch {}
  };

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="grid md:grid-cols-2 gap-12 animate-pulse">
        <div className="aspect-square bg-gray-200 rounded" />
        <div className="space-y-4"><div className="h-6 bg-gray-200 rounded w-1/3" /><div className="h-8 bg-gray-200 rounded w-3/4" /><div className="h-10 bg-gray-200 rounded w-1/4" /></div>
      </div>
    </div>
  );

  if (!product) return (
    <div className="max-w-7xl mx-auto px-4 py-20 text-center" data-testid="product-not-found">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h2>
      <Link to="/products" className="text-[#0F766E] font-semibold hover:underline">Browse All Products</Link>
    </div>
  );

  const discount = product.original_price ? Math.round((1 - product.price / product.original_price) * 100) : 0;
  const cond = conditionLabels[product.condition] || conditionLabels.new_aftermarket;
  const CondIcon = cond.icon;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-8">
        <Link to="/" className="hover:text-[#0F766E]">Home</Link><ChevronRight size={14} />
        <Link to="/products" className="hover:text-[#0F766E]">Products</Link><ChevronRight size={14} />
        <Link to={`/products?category=${product.category}`} className="hover:text-[#0F766E]">{product.category}</Link><ChevronRight size={14} />
        <span className="text-gray-900 font-medium truncate">{product.name}</span>
      </div>

      <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
        {/* Image */}
        <div className="bg-white border rounded overflow-hidden relative">
          <img src={product.image_url || 'https://placehold.co/600x600/f3f4f6/9ca3af?text=No+Image'} alt={product.name} className="w-full aspect-square object-cover" data-testid="product-detail-image" />
          {product.capa_certified && (
            <div className="absolute top-4 left-4 bg-[#0F766E] text-white text-sm font-bold px-3 py-2 rounded flex items-center gap-2">
              <Award size={16} /> CAPA Certified
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          {/* Condition badge */}
          <div className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full mb-3 ${cond.color}`} data-testid="product-condition">
            <CondIcon size={14} /> {cond.label}
          </div>

          <p className="text-[#0F766E] text-sm font-bold uppercase tracking-wider mb-2">{product.brand}</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-2" style={{ fontFamily: "'Chivo', sans-serif" }} data-testid="product-detail-name">
            {product.name}
          </h1>

          {/* SKU & OEM */}
          <div className="flex flex-wrap gap-4 text-xs text-gray-500 mb-4">
            {product.sku && <span>SKU: <strong>{product.sku}</strong></span>}
            {product.oem_part_number && <span>OEM #: <strong className="text-[#0F766E]">{product.oem_part_number}</strong></span>}
            {product.position && <span>Position: <strong>{product.position}</strong></span>}
          </div>

          {/* Price */}
          <div className="flex items-baseline gap-3 mb-4">
            <span className="text-3xl font-black text-[#0F766E]" data-testid="product-detail-price">${product.price.toFixed(2)}</span>
            {product.original_price && (
              <>
                <span className="text-lg text-gray-400 line-through">${product.original_price.toFixed(2)}</span>
                <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded">Save {discount}%</span>
              </>
            )}
          </div>

          {/* Used parts info */}
          {product.condition === 'used_oem' && (
            <div className="bg-amber-50 border border-amber-200 rounded p-4 mb-4" data-testid="used-info">
              <div className="flex items-start gap-3">
                <Info size={18} className="text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-bold text-amber-800">Used OEM Part</p>
                  <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-amber-700">
                    {product.quality_grade && <p>Grade: <strong>{product.quality_grade}</strong></p>}
                    {product.mileage && <p>Mileage: <strong>{product.mileage.toLocaleString()} mi</strong></p>}
                    {product.color && <p>Color: <strong>{product.color}</strong></p>}
                    {product.warranty && <p>Warranty: <strong>{product.warranty}</strong></p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {product.condition === 'refurbished' && (
            <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4" data-testid="refurb-info">
              <div className="flex items-start gap-3">
                <Award size={18} className="text-blue-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-bold text-blue-800">Refurbished Part</p>
                  <p className="text-xs text-blue-700 mt-1">Professionally restored. {product.warranty}</p>
                </div>
              </div>
            </div>
          )}

          {/* Certifications */}
          {(product.capa_certified || product.certifications?.length > 0) && (
            <div className="flex flex-wrap gap-2 mb-4">
              {product.capa_certified && (
                <span className="bg-[#0F766E]/10 text-[#0F766E] text-xs font-bold px-3 py-1.5 rounded flex items-center gap-1"><Award size={12} /> CAPA Certified</span>
              )}
              {product.certifications?.filter(c => c !== 'CAPA').map(cert => (
                <span key={cert} className="bg-gray-100 text-gray-700 text-xs font-bold px-3 py-1.5 rounded">{cert}</span>
              ))}
            </div>
          )}

          {/* Stock */}
          <div className={`flex items-center gap-2 text-sm mb-4 ${product.in_stock ? 'text-green-600' : 'text-red-600'}`}>
            <div className={`w-2 h-2 rounded-full ${product.in_stock ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="font-medium">{product.in_stock ? 'In Stock' : 'Out of Stock'}</span>
          </div>

          {/* Qty & Add */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center border rounded">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2.5 hover:bg-gray-100" data-testid="qty-decrease"><Minus size={16} /></button>
              <span className="w-12 text-center font-semibold text-sm" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-3 py-2.5 hover:bg-gray-100" data-testid="qty-increase"><Plus size={16} /></button>
            </div>
            <button onClick={handleAdd} disabled={!product.in_stock}
              className={`flex-1 font-bold py-3 px-6 rounded transition-all active:scale-95 flex items-center justify-center gap-2 ${added ? 'bg-green-600 text-white' : 'bg-[#F97316] hover:bg-[#EA580C] text-white'} disabled:opacity-40`}
              data-testid="product-add-to-cart-btn">
              {added ? <><Check size={18} /> Added!</> : <><ShoppingCart size={18} /> Add to Cart</>}
            </button>
          </div>

          {/* Description */}
          <div className="border-t pt-6 mb-6">
            <h3 className="font-bold text-sm uppercase tracking-wider text-gray-500 mb-3">Description</h3>
            <p className="text-gray-700 text-sm leading-relaxed" data-testid="product-description">{product.description}</p>
          </div>

          {/* Specifications */}
          <div className="border-t pt-6 mb-6">
            <h3 className="font-bold text-sm uppercase tracking-wider text-gray-500 mb-3">Specifications</h3>
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              {product.material && <><span className="text-gray-500">Material:</span><span className="font-medium text-gray-900">{product.material}</span></>}
              {product.color && <><span className="text-gray-500">Color:</span><span className="font-medium text-gray-900">{product.color}</span></>}
              {product.position && <><span className="text-gray-500">Position:</span><span className="font-medium text-gray-900">{product.position}</span></>}
              {product.warranty && <><span className="text-gray-500">Warranty:</span><span className="font-medium text-gray-900">{product.warranty}</span></>}
              {product.condition && <><span className="text-gray-500">Condition:</span><span className="font-medium text-gray-900">{cond.label}</span></>}
            </div>
          </div>

          {/* Compatibility */}
          {(product.compatible_makes?.length > 0 || product.compatible_years?.length > 0) && (
            <div className="border-t pt-6 mb-6">
              <h3 className="font-bold text-sm uppercase tracking-wider text-gray-500 mb-3">Compatibility</h3>
              <div className="space-y-2 text-sm text-gray-700">
                {product.compatible_makes?.length > 0 && <p><span className="font-semibold">Makes:</span> {product.compatible_makes.join(', ')}</p>}
                {product.compatible_models?.length > 0 && <p><span className="font-semibold">Models:</span> {product.compatible_models.join(', ')}</p>}
                {product.compatible_years?.length > 0 && <p><span className="font-semibold">Years:</span> {Math.min(...product.compatible_years)} - {Math.max(...product.compatible_years)}</p>}
              </div>
            </div>
          )}

          {/* Trust Badges */}
          <div className="border-t pt-6 grid grid-cols-3 gap-3">
            {[
              { icon: Truck, text: 'Free Shipping' },
              { icon: Shield, text: 'Guaranteed Fit' },
              { icon: RotateCcw, text: '30-Day Returns' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <item.icon size={20} className="mx-auto text-[#0F766E] mb-1" />
                <p className="text-xs text-gray-500 font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
